// WAAP Emergency Mitigation — account-level "break glass" config.
//
// A single config object (per strategy type) holding three sub-strategies:
//   - kill_switch (901)        — block all requests
//   - read_only_website (902)  — only GET/HEAD allowed
//   - static_website (903)     — only static HTML/files allowed
//
// Each has an enable flag, a schedule (always / once / periodic), an action set
// and the websites it applies to. The whole object is read-modify-written: the FE
// GETs it, mutates the three strategies, and PUTs it back unchanged otherwise.
// Account-level (like the strategy reads): the BE endpoint is domain-scoped and a
// representative domainId resolves the customer. Backed by the private portal
// (waap2) /emergency_mitigation + /static_files. BE routes (eagle_waf/emergency):
//
//   GET /waap/domains/:id/emergency
//   GET /waap/domains/:id/emergency/static-files
//   PUT /waap/domains/:id/emergency

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

// ---- DTOs (mirror the vendor wire shape) ---------------------------------

/** One action candidate. `action_value` is polymorphic: a status code (400) for
 *  block_code, or an HTML file name for response_html. */
export interface EmergencyAction {
  delay: number;
  action: string; // block_code | response_html
  weight: number;
  action_value: number | string;
}

/** One emergency sub-strategy. */
export interface EmergencyStrategy {
  id: number;
  enable: boolean;
  /** close (Always) | once (Once) | repeat (Periodic). */
  timer_type: string;
  timer_start: number;
  timer_end: number;
  /** [startISO, endISO] for Once. */
  timer_once?: string[] | null;
  /** [startISO, endISO] for Periodic (the time-of-day echo). */
  timer_repeat?: string[] | null;
  timer_repeat_start: string; // "HH:MM:SS"
  timer_repeat_end: string; // "HH:MM:SS"
  /** Active weekday indices, 0=Sun … 6=Sat. */
  timer_repeat_days: number[];
  action_list: EmergencyAction[];
  /** Applied website domain names (server re-derives service_ids on save). */
  sites: string[];
  service_ids: number[];
  // static_website only:
  exception_path?: string;
  paths?: string[];
}

export interface EmergencyConfig {
  kill_switch: EmergencyStrategy;
  read_only_website: EmergencyStrategy;
  static_website: EmergencyStrategy;
}

/** The whole emergency-mitigation object (round-tripped on save). */
export interface EmergencyMitigation {
  id: number;
  type: number;
  config: EmergencyConfig;
  created: number;
  updated: number;
  status: number;
  customer_id: number;
}

/** An uploaded static HTML file (Return-HTML action picker). */
export interface EmergencyStaticFile {
  id: number;
  url: string;
  name: string;
  type: string;
  created: string;
  updated: string;
  status: number;
  customer_id: number;
}

// ---- Catalogs ------------------------------------------------------------

export const EMERGENCY_SCHEDULING: { value: string; label: string }[] = [
  { value: 'close', label: 'Always' },
  { value: 'once', label: 'Once' },
  { value: 'repeat', label: 'Periodic' },
];

export const EMERGENCY_ACTIONS: { value: string; label: string }[] = [
  { value: 'block_code', label: 'Block' },
  { value: 'response_html', label: 'Return HTML' },
];

/** Weekday checkboxes (Mon-first display) → vendor day index (0=Sun … 6=Sat). */
export const EMERGENCY_DAYS: { index: number; label: string }[] = [
  { index: 1, label: 'Monday' },
  { index: 2, label: 'Tuesday' },
  { index: 3, label: 'Wednesday' },
  { index: 4, label: 'Thursday' },
  { index: 5, label: 'Friday' },
  { index: 6, label: 'Saturday' },
  { index: 0, label: 'Sunday' },
];

/** A blank sub-strategy (fallback when the account has no config yet). */
export function emptyEmergencyStrategy(id: number): EmergencyStrategy {
  return {
    id,
    enable: false,
    timer_type: 'close',
    timer_start: 0,
    timer_end: 0,
    timer_once: null,
    timer_repeat: null,
    timer_repeat_start: '00:00:00',
    timer_repeat_end: '00:00:00',
    timer_repeat_days: [0, 1, 2, 3, 4, 5, 6],
    action_list: [{ delay: 0, action: 'block_code', weight: 1, action_value: 400 }],
    sites: [],
    service_ids: [],
  };
}

// ---- Paths + query keys --------------------------------------------------

function emergencyPath(domainId: string, sub = ''): string {
  return `/waap/domains/${domainId}/emergency${sub}`;
}

export const emergencyKeys = {
  config: (domainId: string) => ['waap', 'emergency', domainId, 'config'] as const,
  staticFiles: (domainId: string) => ['waap', 'emergency', domainId, 'static-files'] as const,
};

// ---- Hooks ---------------------------------------------------------------

/** The account's emergency-mitigation config (whole object). */
export function useEmergencyMitigation(domainId: string | undefined) {
  return useQuery({
    queryKey: emergencyKeys.config(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<EmergencyMitigation | null> => {
      const res = await serviceApiV3.get<EmergencyMitigation | null>(emergencyPath(domainId as string));
      return res.data ?? null;
    },
  });
}

/** Uploaded static files for the Return-HTML action picker (kill_switch). */
export function useEmergencyStaticFiles(domainId: string | undefined) {
  return useQuery({
    queryKey: emergencyKeys.staticFiles(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<EmergencyStaticFile[]> => {
      const res = await serviceApiV3.get<EmergencyStaticFile[] | null>(
        emergencyPath(domainId as string, '/static-files'),
      );
      return Array.isArray(res.data) ? res.data : [];
    },
  });
}

/** Save the whole emergency-mitigation object. */
export function useUpdateEmergencyMitigation(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, EmergencyMitigation>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EmergencyMitigation) => {
      const res = await serviceApiV3.put(emergencyPath(domainId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: emergencyKeys.config(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Service-scoped twins (account-level, work with zero websites) ----------
// Mirror the domain-anchored hooks above but build /services/${serviceId}/waap/emergency/...
// so Emergency Mitigation renders without a carrier domain anchor.

function serviceEmergencyPath(serviceId: string, sub = ''): string {
  return `/services/${serviceId}/waap/emergency${sub}`;
}

export const emergencyServiceKeys = {
  config: (serviceId: string) => ['waap', 'emergency', 'service', serviceId, 'config'] as const,
  staticFiles: (serviceId: string) =>
    ['waap', 'emergency', 'service', serviceId, 'static-files'] as const,
};

/** The account's emergency-mitigation config, resolved from the service. */
export function useServiceEmergencyMitigation(serviceId: string | undefined) {
  return useQuery({
    queryKey: emergencyServiceKeys.config(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<EmergencyMitigation | null> => {
      const res = await serviceApiV3.get<EmergencyMitigation | null>(
        serviceEmergencyPath(serviceId as string),
      );
      return res.data ?? null;
    },
  });
}

/** Uploaded static files for the Return-HTML action picker, service-scoped. */
export function useServiceEmergencyStaticFiles(serviceId: string | undefined) {
  return useQuery({
    queryKey: emergencyServiceKeys.staticFiles(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<EmergencyStaticFile[]> => {
      const res = await serviceApiV3.get<EmergencyStaticFile[] | null>(
        serviceEmergencyPath(serviceId as string, '/static-files'),
      );
      return Array.isArray(res.data) ? res.data : [];
    },
  });
}

/** Save the whole emergency-mitigation object, service-scoped. */
export function useUpdateServiceEmergencyMitigation(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, EmergencyMitigation>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EmergencyMitigation) => {
      const res = await serviceApiV3.put(serviceEmergencyPath(serviceId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: emergencyServiceKeys.config(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}
